import { useCallback, useMemo } from "react"
import { useQuery } from "@rocicorp/zero/react"

import { mutators } from "@productlane/zero/mutators"
import { queries } from "@productlane/zero/queries"

import type { NotificationInboxArgs } from "./notification-filter-state"
import { mutateOk, useZero } from "@/lib/zero"
import { formatUnreadCount } from "./group-notifications"

export type NotificationView = "inbox" | "unread" | "mentions" | "archived"

export const NOTIFICATION_PAGE_SIZE = 50

export function useNotificationInbox(args: NotificationInboxArgs) {
	const [rows, result] = useQuery(queries.notificationInbox(args))
	return { rows, isLoaded: result.type === "complete" }
}

export type NotificationListRow = ReturnType<
	typeof useNotificationInbox
>["rows"][number]

/** Newest 100 unread rows: drives the badge, desktop popups and mark-on-open. */
export function useUnreadNotifications() {
	const [rows, result] = useQuery(queries.notificationUnread())
	const isLoaded = result.type === "complete"
	return useMemo(
		() => ({
			rows,
			isLoaded,
			count: rows.length,
			formattedCount: formatUnreadCount(rows.length),
		}),
		[rows, isLoaded],
	)
}

/** Unread mentions, capped like the unread badge: the Mentions tab count and the nav's red dot. */
export function useUnreadMentions() {
	const [rows] = useQuery(queries.notificationUnreadMentions())
	return useMemo(
		() => ({
			count: rows.length,
			formattedCount: formatUnreadCount(rows.length),
		}),
		[rows],
	)
}

export function useNotificationActions() {
	const zero = useZero()

	const setRead = useCallback(
		(ids: string[], read: boolean) =>
			mutateOk(
				zero.mutate(
					mutators.notifications.setRead({ ids, read, at: Date.now() }),
				),
			),
		[zero],
	)
	const setArchived = useCallback(
		(ids: string[], archived: boolean) =>
			mutateOk(
				zero.mutate(
					mutators.notifications.setArchived({
						ids,
						archived,
						at: Date.now(),
					}),
				),
			),
		[zero],
	)
	const remove = useCallback(
		(ids: string[]) =>
			mutateOk(zero.mutate(mutators.notifications.remove({ ids }))),
		[zero],
	)
	const markAllRead = useCallback(
		() =>
			mutateOk(
				zero.mutate(mutators.notifications.markAllRead({ at: Date.now() })),
			),
		[zero],
	)
	const removeAll = useCallback(
		() =>
			mutateOk(
				zero.mutate(mutators.notifications.removeAll({ at: Date.now() })),
			),
		[zero],
	)
	const removeCompleted = useCallback(
		() =>
			mutateOk(
				zero.mutate(mutators.notifications.removeCompleted({ at: Date.now() })),
			),
		[zero],
	)
	const removeAllRead = useCallback(
		() =>
			mutateOk(
				zero.mutate(mutators.notifications.removeAllRead({ at: Date.now() })),
			),
		[zero],
	)
	const markThreadRead = useCallback(
		(threadId: string) =>
			mutateOk(
				zero.mutate(
					mutators.notifications.markThreadRead({ threadId, at: Date.now() }),
				),
			),
		[zero],
	)

	return {
		setRead,
		setArchived,
		remove,
		markAllRead,
		removeAll,
		removeAllRead,
		removeCompleted,
		markThreadRead,
	}
}
